import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, InputGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { externalDestinationsApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface ExternalDestination {
  id: string
  name: string
  number: string
  description: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

const emptyForm = {
  name: '',
  number: '',
  description: '',
  is_active: true
}

export default function ExternalDestinations() {
  const confirm = useConfirm()
  const [destinations, setDestinations] = useState<ExternalDestination[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingDest, setEditingDest] = useState<ExternalDestination | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [search, setSearch] = useState('')

  useEffect(() => {
    loadDestinations()
  }, [search])

  const loadDestinations = async () => {
    try {
      const response = await externalDestinationsApi.list({ q: search || undefined })
      setDestinations(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden der externen Ziele')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingDest(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (dest: ExternalDestination) => {
    setEditingDest(dest)
    setFormData({
      name: dest.name,
      number: dest.number,
      description: dest.description || '',
      is_active: dest.is_active
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingDest) {
        await externalDestinationsApi.update(editingDest.id, formData)
        toast.success('Externes Ziel aktualisiert')
      } else {
        await externalDestinationsApi.create(formData)
        toast.success('Externes Ziel erstellt')
      }
      setShowModal(false)
      loadDestinations()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern')
    }
  }

  const handleDelete = async (dest: ExternalDestination) => {
    const confirmed = await confirm({
      title: 'Externes Ziel löschen',
      message: `"${dest.name}" (${dest.number}) wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await externalDestinationsApi.delete(dest.id)
      toast.success('Externes Ziel gelöscht')
      loadDestinations()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('de-DE')
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4><i className="bi bi-telephone-outbound-fill me-2"></i>Externe Ziele</h4>
          <p className="text-muted mb-0">
            Häufig verwendete externe Telefonnummern für Rufweiterleitungen und Routing
          </p>
        </div>
        <div className="d-flex gap-2">
          <InputGroup style={{ width: '250px' }}>
            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
            <Form.Control
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Button variant="primary" onClick={openCreateModal}>
            <i className="bi bi-plus-lg me-1"></i> Neues Ziel
          </Button>
        </div>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Telefonnummer</th>
              <th>Beschreibung</th>
              <th>Erstellt</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-4">Laden...</td></tr>
            ) : destinations.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-4 text-muted">
                {search ? 'Keine Treffer' : 'Noch keine externen Ziele vorhanden'}
              </td></tr>
            ) : (
              destinations.map(dest => (
                <tr key={dest.id}>
                  <td>
                    <i className="bi bi-telephone-outbound-fill text-primary me-2"></i>
                    <strong>{dest.name}</strong>
                  </td>
                  <td>
                    <code>{dest.number}</code>
                  </td>
                  <td>{dest.description || <span className="text-muted">-</span>}</td>
                  <td className="small text-muted">{formatDate(dest.created_at)}</td>
                  <td>
                    <Badge bg={dest.is_active ? 'success' : 'secondary'}>
                      {dest.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(dest)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(dest)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-telephone-outbound-fill me-2"></i>
            {editingDest ? 'Externes Ziel bearbeiten' : 'Neues externes Ziel'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="z.B. Mobilnummer Chef, Support Hotline"
              />
              <Form.Text className="text-muted">
                Dieser Name wird im Ziel-Dialog angezeigt
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Telefonnummer *</Form.Label>
              <Form.Control
                value={formData.number}
                onChange={(e) => setFormData({...formData, number: e.target.value})}
                required
                placeholder="z.B. 0041762161443 oder 0762161443"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optionale Beschreibung"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_active"
                label="Aktiv"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
              <Form.Text className="text-muted">
                Inaktive Ziele werden im Ziel-Dialog nicht angezeigt
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingDest ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
